import React, { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { styles } from "./style";

import GenericCardHeader from "@/components/GenericCardHeader";
import AppointmentDetail from "./AppointmentDetail";
import Button from "@/components/Button";
import GetImages from "@/components/GetImages";
import Confirmation from "@/components/Confirmation";
import MakeAppointment from "@/components/FindDoctorCard/MakeAppointment";
import RescheduleAppointment from "@/components/MakeAppointment";
import Dialog from "@/components/Dialog";

import {
  IAppointmentCreation,
  IAppointmetEnums,
  IDoctorAvailabilityPlatform,
  IDoctorProfileStatus,
  IDoctorScheduleConsultationType,
  IMakeAppointmentEvent,
} from "@/models/doctor";
import { IButtonVariant } from "@/models/button";
import {
  IAllOrderTypeEnums,
  IOrderStatus,
} from "@/models/ApiRequestResponse/patient";
import {
  IAgoraTokenForVideoCallPermissionEnums,
  IModalKeyEnums,
} from "@/models/permission";
import { ISessionKeys } from "@/models";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchRTCTokenAgora,
  fetchRTMTokenAgora,
  setChannelName,
} from "@/store/slices/videoAppointment";
import {
  config,
  handleIsInfiniteScrollFilterChanges,
  handleIsShowSuccessModal,
  setIsShowUniversalSearch,
} from "@/store/slices/config";
import { fetchDoctorProfile } from "@/store/slices/doctor/profile";
import { checkoutDetail } from "@/store/slices/patient/findDoctor";
import {
  fetchAllOrdersList,
  fetchHomeModules,
  setAllOrders,
} from "@/store/slices/patient/home";
import {
  appointmentDetails,
  setAppointmentDetail,
} from "@/store/slices/doctor/appointment";

import {
  doctorDegreeName,
  languagesListInfo,
  openClinicMapLocation,
} from "@/utils";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { ShimmerButton } from "@/utils/common";
import { supportNumber } from "@/utils/Config";
import { cancelScheduleAppointmentsApi } from "@/utils/apis/doctor/appointment";
import { IAPIStatusCode } from "@/utils/apis/APIEndpointConfig";
import { INotifyEnum, notify } from "@/utils/toaster";
import { storage } from "@/utils/Storage";

import { IProps } from "./type";

const AppointmentCard: React.FC<IProps> = (props) => {
  const [confirmCancelAppointment, setConfirmCancelAppointment] =
    useState<boolean>(false);
  const [isAppointmentCreation, setIsAppointmentCreation] =
    useState<IAppointmentCreation>({
      creation: false,
      success: false,
    });
  const [rescheduleAppointment, setRescheduleAppointment] =
    useState<IAppointmentCreation>({
      creation: false,
      success: false,
    });
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [platform, setPlatform] = useState<IDoctorAvailabilityPlatform>();

  const {
    id,
    status,
    consultation_type,
    experience_years,
    name,
    speciality,
    liveQueue,
    photo,
    profile_photo,
    qualification_details,
    slot_start_time,
    slot_time,
    slot_no,
    address,
    slot_end_time,
    latitude,
    longitude,
    doctor_id,
    memberid,
    patientName,
    gender,
    age,
    language,
    filters,
  } = props;

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const isSmallScreen = useMemo(
    () => isMobile || isTablet,
    [isMobile, isTablet]
  );

  const { isShowSuccessModal, isShowUniversalSearch } = useAppSelector(config);
  const { appointmentDetail } = useAppSelector(appointmentDetails);

  const userId = storage.getSessionToken(ISessionKeys.USER_ID);

  const isBeforeAllowedTime = dayjs().isBefore(
    dayjs(slot_start_time).subtract(5, "minute")
  );
  const isAfterSlotEndTime = dayjs().isAfter(
    dayjs(slot_end_time).add(1, "month")
  );
  const isSlotEndTime = dayjs().isAfter(dayjs(slot_end_time));

  const isButtonDisabledForVideoCallJoin = isSlotEndTime || isBeforeAllowedTime;
  const isButtonDisabled = isAfterSlotEndTime || isBeforeAllowedTime;

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const permissionRestrictionHandler = usePermissionRestriction();

  const openConfirmCancelAppointment = (): void =>
    setConfirmCancelAppointment(!confirmCancelAppointment);

  const closeRescheduleModal = (): void => {
    const rescheduleModal: IAppointmentCreation = {
      creation: false,
      success: false,
    };
    for (const key in rescheduleAppointment) {
      if (key in rescheduleModal) {
        rescheduleModal[key as keyof IAppointmentCreation] = false;
      }
    }
    setRescheduleAppointment(rescheduleModal);
  };

  const joinVideoCall = (bookingId: string): void => {
    navigate("/join-room");

    const isRTCTokenPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.VIDEO_CALL,
      IAgoraTokenForVideoCallPermissionEnums.GENERATE_VIDEO_CALL_TOKEN_FOR_APP
    );
    if (!isRTCTokenPermissionGranted) return;
    dispatch(fetchRTCTokenAgora(bookingId));

    const isRTMTokenPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.VIDEO_CALL,
      IAgoraTokenForVideoCallPermissionEnums.GENERATE_VIDEO_CALL_TOKEN_FOR_WEB
    );
    if (!isRTMTokenPermissionGranted) return;
    dispatch(fetchRTMTokenAgora(bookingId));
    dispatch(setChannelName(bookingId));
  };

  const closeAppointmentModal = () => {
    const schedulesValues: any = {};
    for (let key in isAppointmentCreation) {
      schedulesValues[key] = false;
    }

    if (isShowSuccessModal) {
      dispatch(handleIsShowSuccessModal());
    }
    setIsAppointmentCreation(schedulesValues);
  };

  const openAppointmentModalHandler = (type: IAppointmetEnums) => {
    const schedulesValues: any = {};
    for (let key in isAppointmentCreation) {
      schedulesValues[key] = false;
    }
    switch (type) {
      case IAppointmetEnums.CREATION:
        setIsAppointmentCreation({
          ...schedulesValues,
          creation: true,
        });
        return;
      case IAppointmetEnums.SUCCESS:
        setIsAppointmentCreation({
          ...schedulesValues,
          success: true,
        });
        return;
      default:
        closeAppointmentModal();
        return;
    }
  };

  const openAppointmentRescheduleDetailHandler = async (
    type: IAppointmetEnums
  ) => {
    switch (type) {
      case IAppointmetEnums.CREATION:
        setRescheduleAppointment({
          ...rescheduleAppointment,
          creation: true,
        });
        return;
      case IAppointmetEnums.SUCCESS:
        setRescheduleAppointment({
          ...rescheduleAppointment,
          success: true,
        });
        return;
      default:
        closeRescheduleModal();
        return;
    }
  };

  const selectedTabOpenAppointmentModalHandler = (
    type: IAppointmetEnums,
    tab: IMakeAppointmentEvent,
    doctorPlatform: IDoctorAvailabilityPlatform
  ) => {
    dispatch(fetchDoctorProfile(doctor_id));
    dispatch(
      checkoutDetail({
        doctorId: doctor_id,
        doctorImage: profile_photo ?? photo,
        doctorName: name,
        bio: `${experience_years} yrs • ${speciality} • ${doctorDegreeName(
          qualification_details
        )}`,
        languages: language.map((lang) => languagesListInfo[lang]).join(" • "),
        type:
          tab === 0
            ? IDoctorAvailabilityPlatform.VIDEO_CALL
            : IDoctorAvailabilityPlatform.IN_CLINIC,
      })
    );
    setSelectedTab(tab);
    setPlatform(doctorPlatform);
    openAppointmentModalHandler(type);
  };

  const cancelAppointmentHandler = async () => {
    try {
      const cancelAppointmentsResponse = await cancelScheduleAppointmentsApi({
        schedules: [],
        appointments: [id],
      });
      if (cancelAppointmentsResponse.statusCode === IAPIStatusCode.SUCCESS) {
        dispatch(setAllOrders([]));
        dispatch(handleIsInfiniteScrollFilterChanges());
        dispatch(fetchAllOrdersList({ ...filters, offset: "0" }));
        dispatch(fetchHomeModules({ userId: userId! }));
        openConfirmCancelAppointment();
        notify(INotifyEnum.SUCCESS, cancelAppointmentsResponse.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const rescheduleAppointmentHandler = () => {
    dispatch(
      setAppointmentDetail({
        ...appointmentDetail,
        status,
        id,
      })
    );
    openAppointmentRescheduleDetailHandler(IAppointmetEnums.CREATION);
    dispatch(
      checkoutDetail({
        doctorId: doctor_id,
        doctorImage: profile_photo ?? photo,
        doctorName: name,
        bio: `${experience_years} yrs • ${speciality} • ${doctorDegreeName(
          qualification_details
        )}`,
        languages: language.map((lang) => languagesListInfo[lang]).join(" • "),
        type: platform,
      })
    );
  };

  const chatRedirectionHandler = () => {
    navigate("/chat", {
      state: {
        channelId: `app_${doctor_id}_${memberid}`,
      },
    });
    if (isShowUniversalSearch) {
      dispatch(setIsShowUniversalSearch(false));
    }
  };
  return (
    <Grid item sx={styles.cardContainer}>
      <GenericCardHeader
        orderStatus={status}
        doctorId={doctor_id}
        image={photo ?? profile_photo}
        name={name}
        bio={`${experience_years} yrs • ${speciality} ${
          qualification_details && `• ${qualification_details[0]?.degree_name}`
        }`}
        status={IDoctorProfileStatus.VERIFIED}
        liveQueue={liveQueue}
        date={slot_start_time}
        orderType={IAllOrderTypeEnums.APPOINTMENT}
      />
      <Grid item sx={styles.borderLine}></Grid>
      <AppointmentDetail
        startTime={slot_start_time ?? ""}
        endTime={slot_end_time}
        slotDuration={slot_time ?? 0}
        type={consultation_type || IDoctorScheduleConsultationType.VIDEO_CALL}
        slotNo={slot_no}
        clinicName={`${address}`}
        patientDetail={`${patientName}, ${age} yrs, ${gender}`}
        latitude={latitude}
        longitude={longitude}
        status={status}
      />
      <Grid item sx={styles.buttonContainer}>
        {(status === IOrderStatus.PENDING ||
          status === IOrderStatus.UPCOMING) && (
          <>
            <Button
              variant={IButtonVariant.WHITE}
              btnTrigger={openConfirmCancelAppointment}
            >
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
            {consultation_type ===
              IDoctorScheduleConsultationType.IN_CLINIC && (
              <>
                <Button
                  variant={IButtonVariant.WHITE}
                  btnTrigger={() =>
                    openClinicMapLocation(latitude ?? 0, longitude ?? 0)
                  }
                >
                  <GetImages
                    name="LocationAccessSmallIcon"
                    width="26"
                    height="20"
                  />
                  <FormattedMessage id="direction" defaultMessage="Direction" />
                </Button>
                <Grid
                  item
                  sx={{
                    ...styles.contactSupportButton,
                    ...styles.contactSupportOrChatButton,
                  }}
                  onClick={() =>
                    (window.location.href = `tel:${supportNumber}`)
                  }
                >
                  <GetImages name="ContactSupportIcon" width="16" height="16" />
                </Grid>
              </>
            )}
          </>
        )}
        {status === IOrderStatus.COMPLETED && (
          <Grid item sx={styles.openChat}>
            <Button
              disabled={isButtonDisabled}
              variant={IButtonVariant.WHITE}
              btnTrigger={chatRedirectionHandler}
            >
              <GetImages
                name="OpenChatIcon"
                width="26"
                height="20"
                fill={
                  isButtonDisabled ? "var(--neutral-100)" : "var(--orange-500)"
                }
              />
              <FormattedMessage id="open_chat" defaultMessage="Open Chat" />
            </Button>
            <Button
              variant={IButtonVariant.WHITE}
              btnTrigger={() =>
                selectedTabOpenAppointmentModalHandler(
                  IAppointmetEnums.CREATION,
                  IMakeAppointmentEvent.VIDEO_CALL,
                  platform || IDoctorAvailabilityPlatform.BOTH
                )
              }
            >
              <GetImages
                name="ScheduleSelectedSmallIcon"
                width="26"
                height="20"
              />
              <FormattedMessage id="book_again" defaultMessage="Book Again" />
            </Button>
          </Grid>
        )}

        {status === IOrderStatus.CANCELLED && (
          <Button
            variant={IButtonVariant.WHITE}
            btnTrigger={rescheduleAppointmentHandler}
          >
            <GetImages
              name="ScheduleSelectedSmallIcon"
              width="26"
              height="20"
            />
            <FormattedMessage id="reschedule" defaultMessage="Reschedule" />
          </Button>
        )}
        {(status === IOrderStatus.PENDING ||
          status === IOrderStatus.UPCOMING) && (
          <>
            {consultation_type ===
              IDoctorScheduleConsultationType.VIDEO_CALL && (
              <>
                <ShimmerButton
                  variant="contained"
                  disabled={isButtonDisabledForVideoCallJoin}
                  onClick={(): void => joinVideoCall(id)}
                >
                  <GetImages name="VideoWhiteIcon" width="26" height="20" />
                  <FormattedMessage id="join_call" defaultMessage="Join Call" />
                </ShimmerButton>
                <Grid
                  item
                  sx={{
                    ...styles.contactSupportOrChatButton(isButtonDisabled),
                    ...styles.chatButton,
                  }}
                  onClick={() => {
                    if (isButtonDisabled) return;
                    chatRedirectionHandler();
                  }}
                >
                  <GetImages name="ChatOrderIcon" width="16" height="16" />
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
      {confirmCancelAppointment && (
        <Confirmation
          isOpen={confirmCancelAppointment}
          closeHandler={openConfirmCancelAppointment}
          title="cancel_appointments"
          alterMessage={"confirm_cancel_appoinment"}
          qnsType={false}
          actionHandler={cancelAppointmentHandler}
        />
      )}

      {rescheduleAppointment.creation && (
        <RescheduleAppointment
          doctorId={doctor_id}
          isReschedule={rescheduleAppointment.creation}
          isOpen={rescheduleAppointment.creation}
          platform={
            consultation_type === IDoctorScheduleConsultationType.VIDEO_CALL
              ? IDoctorAvailabilityPlatform.VIDEO_CALL
              : IDoctorAvailabilityPlatform.IN_CLINIC
          }
          selectedTab={
            consultation_type === IDoctorScheduleConsultationType.VIDEO_CALL
              ? IMakeAppointmentEvent.VIDEO_CALL
              : IMakeAppointmentEvent.IN_CLINIC
          }
          setSelectedTab={setSelectedTab}
          closeScheduleModal={closeRescheduleModal}
          openScheduleModalHandler={openAppointmentRescheduleDetailHandler}
        />
      )}

      {isSmallScreen ? (
        <>
          {isAppointmentCreation.creation && (
            <MakeAppointment
              open={isAppointmentCreation.creation}
              doctorId={doctor_id}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              openScheduleModalHandler={openAppointmentModalHandler}
              onClose={closeAppointmentModal}
              platform={platform}
            />
          )}
        </>
      ) : (
        <Dialog
          closeHandler={closeAppointmentModal}
          isOpen={isAppointmentCreation.creation}
          maxWidth="xs"
          fullWidth
          paperProps={{
            sx: {
              backgroundColor: "var(--neutral-100) !important",
              maxWidth: isShowSuccessModal ? "35.5rem" : "40rem",
              minHeight: "10rem",
              ...(isShowSuccessModal
                ? {
                    borderRadius: "1.2rem",
                  }
                : {}),
            },
          }}
        >
          <MakeAppointment
            open={isAppointmentCreation.creation}
            doctorId={doctor_id}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            openScheduleModalHandler={openAppointmentModalHandler}
            onClose={closeAppointmentModal}
            platform={platform}
          />
        </Dialog>
      )}
    </Grid>
  );
};

export default AppointmentCard;
